import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, CircularProgress, Alert, Grid, Card, CardContent, IconButton, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';
import { useEnum, ENUM_NAMES } from '../hooks/useEnum';

interface Surgery {
  id: number;
  pet_name: string;
  owner_name: string;
  procedure_name: string;
  procedure_category: string;
  scheduled_date: string;
  start_time: string | null;
  end_time: string | null;
  duration_minutes: number | null;
  status: string;
  surgeon_name: string;
  pre_op_notes: string;
  post_op_notes: string;
  outcome: string;
}

interface Pet { id: number; name: string; }

const statusColor = (s: string): 'info' | 'warning' | 'success' | 'error' | 'default' => {
  if (s === 'Scheduled') return 'info';
  if (s === 'In Progress') return 'warning';
  if (s === 'Completed') return 'success';
  if (s === 'Cancelled') return 'error';
  return 'default';
};

const emptyForm = {
  pet_id: '',
  procedure_name: '',
  procedure_category: 'Elective',
  scheduled_date: new Date().toISOString().slice(0, 10),
  surgeon_id: '',
  pre_op_notes: '',
};

const SurgeriesPage = () => {
  const [surgeries, setSurgeries] = useState<Surgery[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [completeDialog, setCompleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [completeForm, setCompleteForm] = useState({ post_op_notes: '', outcome: '', complications: '' });
  const [deleteSurgery, setDeleteSurgery] = useState<Surgery | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { values: surgeryCategories } = useEnum(ENUM_NAMES.SURGERY_CATEGORIES);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [surgRes, petsRes] = await Promise.all([
        api.get<{ data: Surgery[] }>(API_ENDPOINTS.SURGERIES.LIST),
        api.get<{ data: Pet[] }>(API_ENDPOINTS.PATIENTS.LIST),
      ]);
      setSurgeries(Array.isArray(surgRes.data) ? surgRes.data : []);
      setPets(Array.isArray(petsRes.data) ? petsRes.data : []);
    } catch {
      setError('Failed to load surgeries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.pet_id || !formData.procedure_name || !formData.scheduled_date) {
      setError('Patient, procedure name, and scheduled date are required');
      return;
    }
    try {
      setSaving(true);
      await api.post(API_ENDPOINTS.SURGERIES.CREATE, {
        ...formData,
        pet_id: parseInt(formData.pet_id),
        surgeon_id: formData.surgeon_id ? parseInt(formData.surgeon_id) : undefined,
      });
      setDialogOpen(false);
      setFormData(emptyForm);
      fetchData();
    } catch {
      setError('Failed to create surgery');
    } finally {
      setSaving(false);
    }
  };

  const handleStart = async (id: number) => {
    try {
      await api.put(API_ENDPOINTS.SURGERIES.UPDATE(id), { status: 'In Progress', start_time: new Date().toISOString() });
      fetchData();
    } catch {
      setError('Failed to update surgery');
    }
  };

  const handleComplete = async () => {
    if (!completeDialog.id) return;
    try {
      setSaving(true);
      await api.patch(API_ENDPOINTS.SURGERIES.COMPLETE(completeDialog.id), {
        ...completeForm,
        end_time: new Date().toISOString(),
      });
      setCompleteDialog({ open: false, id: null });
      setCompleteForm({ post_op_notes: '', outcome: '', complications: '' });
      fetchData();
    } catch {
      setError('Failed to complete surgery');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteSurgery) return;
    setDeleting(true);
    try {
      await api.delete(`/surgeries/${deleteSurgery.id}`);
      setDeleteSurgery(null);
      fetchData();
    } catch { setError('Failed to delete surgery'); } finally { setDeleting(false); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const scheduledToday = surgeries.filter(s => s.scheduled_date?.slice(0, 10) === today && s.status === 'Scheduled').length;
  const inProgress = surgeries.filter(s => s.status === 'In Progress').length;
  const completedMonth = surgeries.filter(s => {
    const d = s.scheduled_date?.slice(0, 7);
    return d === today.slice(0, 7) && s.status === 'Completed';
  }).length;

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Surgeries</Typography>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary">Scheduled Today</Typography>
              <Typography variant="h4">{scheduledToday}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary">In Progress</Typography>
              <Typography variant="h4" color="warning.main">{inProgress}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary">Completed This Month</Typography>
              <Typography variant="h4" color="success.main">{completedMonth}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Schedule Surgery
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Procedure</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Surgeon</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {surgeries.map(s => (
              <TableRow key={s.id} hover>
                <TableCell>{s.scheduled_date ? new Date(s.scheduled_date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>{s.pet_name}</TableCell>
                <TableCell>{s.owner_name}</TableCell>
                <TableCell><strong>{s.procedure_name}</strong></TableCell>
                <TableCell><Chip label={s.procedure_category} size="small" variant="outlined" /></TableCell>
                <TableCell>{s.surgeon_name || '-'}</TableCell>
                <TableCell><Chip label={s.status} size="small" color={statusColor(s.status)} /></TableCell>
                <TableCell>
                  {s.status === 'Scheduled' && (
                    <Tooltip title="Start Surgery">
                      <IconButton size="small" color="warning" onClick={() => handleStart(s.id)}>
                        <StartIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {s.status === 'In Progress' && (
                    <Tooltip title="Complete Surgery">
                      <IconButton size="small" color="success"
                        onClick={() => { setCompleteDialog({ open: true, id: s.id }); }}>
                        <CompleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(s.status === 'Scheduled' || s.status === 'Cancelled') && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteSurgery(s)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {surgeries.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">No surgeries found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Surgery</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Patient *</InputLabel>
            <Select value={formData.pet_id} label="Patient *"
              onChange={e => setFormData({ ...formData, pet_id: String(e.target.value) })}>
              {pets.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Procedure Name *" value={formData.procedure_name}
            onChange={e => setFormData({ ...formData, procedure_name: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select value={formData.procedure_category} label="Category"
              onChange={e => setFormData({ ...formData, procedure_category: e.target.value })}>
              {surgeryCategories.map(c => <MenuItem key={c.code} value={c.display_name}>{c.display_name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Scheduled Date *" type="date"
            InputLabelProps={{ shrink: true }} value={formData.scheduled_date}
            onChange={e => setFormData({ ...formData, scheduled_date: e.target.value })} />
          <TextField fullWidth margin="normal" label="Pre-Op Notes" multiline rows={3}
            value={formData.pre_op_notes}
            onChange={e => setFormData({ ...formData, pre_op_notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={completeDialog.open} onClose={() => setCompleteDialog({ open: false, id: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Surgery</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Outcome" value={completeForm.outcome}
            onChange={e => setCompleteForm({ ...completeForm, outcome: e.target.value })} />
          <TextField fullWidth margin="normal" label="Complications" value={completeForm.complications}
            onChange={e => setCompleteForm({ ...completeForm, complications: e.target.value })} />
          <TextField fullWidth margin="normal" label="Post-Op Notes" multiline rows={3}
            value={completeForm.post_op_notes}
            onChange={e => setCompleteForm({ ...completeForm, post_op_notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialog({ open: false, id: null })} disabled={saving}>Cancel</Button>
          <Button onClick={handleComplete} variant="contained" color="success" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Mark Complete'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete confirmation */}
      <Dialog open={!!deleteSurgery} onClose={() => setDeleteSurgery(null)}>
        <DialogTitle>Delete Surgery</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteSurgery?.procedure_name}</strong> for <strong>{deleteSurgery?.pet_name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSurgery(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurgeriesPage;
